/**
 * TheGraphWidget Component
 *
 * Compact widget showing The Graph GRT delegation status for the Portfolio Dashboard
 * - Shows total delegated GRT and pending rewards
 * - Clickable to navigate to TheGraphMonitor page
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Icons } from '../Icons';
import { thegraphAPI } from '../../services/thegraphApi';
import { MONITOR_SYNC_EVENT } from '../../hooks/useMonitorWalletsSync';

const WALLETS_STORAGE_KEY = 'thegraph_wallets';

// The Graph brand color
const THEGRAPH_COLOR = '#6747ED';

interface TheGraphWidgetProps {
  onNavigate?: (pageId: string) => void;
}

export function TheGraphWidget({ onNavigate }: TheGraphWidgetProps) {
  const { t } = useTranslation();
  const [wallets, setWallets] = useState<string[]>([]);

  // Load wallets from localStorage
  const loadWallets = useCallback(() => {
    const savedWallets = localStorage.getItem(WALLETS_STORAGE_KEY);
    if (savedWallets) {
      try {
        const parsed = JSON.parse(savedWallets);
        if (Array.isArray(parsed)) {
          setWallets(parsed);
        }
      } catch (e) {
        console.error('Error loading The Graph wallets:', e);
      }
    }
  }, []);

  // Load wallets on mount and when sync completes
  useEffect(() => {
    loadWallets();

    // Listen for monitor sync events to reload wallets
    const handleSyncComplete = () => {
      console.log('[TheGraphWidget] Sync event received, reloading wallets...');
      loadWallets();
    };

    window.addEventListener(MONITOR_SYNC_EVENT, handleSyncComplete);
    return () => {
      window.removeEventListener(MONITOR_SYNC_EVENT, handleSyncComplete);
    };
  }, [loadWallets]);

  // Fetch wallet data
  const { data: walletsData, isLoading } = useQuery({
    queryKey: ['thegraph-widget-data', wallets],
    queryFn: async () => {
      const results = await Promise.all(
        wallets.map((w) => thegraphAPI.getWalletData(w))
      );
      return results.filter((r) => r !== null);
    },
    enabled: wallets.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1,
  });

  // Calculate totals
  const totalDelegated = walletsData?.reduce((sum, w) => sum + w.totalDelegated, 0) || 0;
  const pendingRewards = walletsData?.reduce((sum, w) => sum + w.pendingRewards, 0) || 0;
  const delegationsCount = walletsData?.reduce((sum, w) => sum + w.delegationsCount, 0) || 0;

  const handleClick = () => {
    if (onNavigate) {
      onNavigate('thegraph-monitor');
    }
  };

  // Format GRT amounts
  const formatGRT = (amount: number) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(2)}M`;
    }
    if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    }
    return amount.toFixed(0);
  };

  const hasRewards = pendingRewards > 0;
  const isConfigured = wallets.length > 0;

  // Show skeleton/placeholder if not configured
  if (!isConfigured) {
    return (
      <div
        onClick={handleClick}
        style={{
          background: `linear-gradient(135deg, ${THEGRAPH_COLOR}10, ${THEGRAPH_COLOR}08)`,
          borderRadius: '12px',
          padding: '12px 16px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          border: `1px dashed ${THEGRAPH_COLOR}40`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '200px',
          opacity: 0.7,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.borderColor = `${THEGRAPH_COLOR}60`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.7';
          e.currentTarget.style.borderColor = `${THEGRAPH_COLOR}40`;
        }}
        title={t('thegraph.widget.clickToConfigure', 'Click to configure The Graph Monitor')}
      >
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: `${THEGRAPH_COLOR}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icons.TheGraph size={18} color={`${THEGRAPH_COLOR}80`} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)', opacity: 0.6 }}>
              GRT Delegations
            </span>
          </div>
          <span style={{ fontSize: '11px', color: 'var(--muted)', opacity: 0.6 }}>
            {t('thegraph.widget.clickToConfigure', 'Click to configure')}
          </span>
        </div>
        <Icons.Plus size={16} style={{ color: `${THEGRAPH_COLOR}80`, flexShrink: 0 }} />
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      style={{
        background: `linear-gradient(135deg, ${THEGRAPH_COLOR}18, ${THEGRAPH_COLOR}0C)`,
        borderRadius: '12px',
        padding: '12px 16px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: `1px solid ${THEGRAPH_COLOR}30`,
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        minWidth: '200px',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 4px 12px ${THEGRAPH_COLOR}30`;
        e.currentTarget.style.borderColor = `${THEGRAPH_COLOR}50`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = `${THEGRAPH_COLOR}30`;
      }}
      title={t('thegraph.widget.clickToOpen', 'Click to open The Graph Monitor')}
    >
      {/* Icon */}
      <div
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '10px',
          background: `linear-gradient(135deg, ${THEGRAPH_COLOR}, #4F35C5)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icons.TheGraph size={18} color="white" />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)' }}>
            GRT Delegations
          </span>
          {/* Status indicator */}
          {isLoading ? (
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#6b7280',
                animation: 'pulse 1s infinite',
              }}
            />
          ) : totalDelegated > 0 ? (
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: hasRewards ? '#10b981' : THEGRAPH_COLOR,
                boxShadow: hasRewards ? '0 0 8px #10b981' : `0 0 8px ${THEGRAPH_COLOR}`,
              }}
            />
          ) : null}
        </div>

        {/* Balance and rewards info */}
        {isLoading ? (
          <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
            {t('common.loading', 'Loading...')}
          </span>
        ) : totalDelegated > 0 ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Icons.TheGraph size={10} color={THEGRAPH_COLOR} />
              <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                {formatGRT(totalDelegated)}
              </span>
            </div>
            {delegationsCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Icons.Users size={10} style={{ color: 'var(--muted)' }} />
                <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                  {delegationsCount}
                </span>
              </div>
            )}
            {pendingRewards > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Icons.Gift size={10} style={{ color: '#10b981' }} />
                <span style={{ fontSize: '11px', color: '#10b981' }}>
                  +{formatGRT(pendingRewards)}
                </span>
              </div>
            )}
          </div>
        ) : (
          <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
            {t('thegraph.widget.noBalance', 'No delegations')}
          </span>
        )}
      </div>

      {/* Arrow */}
      <Icons.ChevronRight size={16} style={{ color: 'var(--muted)', flexShrink: 0 }} />

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
